/* eslint-disable eqeqeq */

/** @format */
import React, { useEffect, useState } from 'react';
import { Grid, Button, Typography, CircularProgress } from '@mui/material';
import { Alert } from '@mui/material';
import {
  DatePickerPod,
  InputV2,
  ButtonLoading,
  AuthService,
  ConfirmDialog,
  InfoBox,
  Icons,
} from 'components';
import { LabelLine } from 'components/InputV2/components';
import { useFormContext } from 'react-hook-form';
import { DatePicker } from 'components/LegacyPickers';
import jMoment from 'moment-jalaali';
import persianJs from 'persianjs';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { useQuery } from 'hooks';
import { useNavigate } from 'react-router-dom';
import createToast from 'components/Toast/Toast';
import OwnerServices from './services/OwnerServices';

const OwnerInfo = ({
  birthDateOwner,
  isLegal,
  financialLevel,
  disableCellPhone,
  showChangingOwner,
  currentOwner,
  submittingBusiness = false, // for submit
  loading,
  unverifiedOwner,
  onSubmit = () => { },
  setNewOwnerId = () => { },
  onShowChangeOwner = () => { },
  onCancelOwnerChange = () => { },
}) => {
  const methods = useFormContext();
  const [birthDate, setBirthDate] = useState(null); //
  const [loginLoading, setLoginLoading] = useState(false);
  const [loadingVerifyOwner, setLoadingVerifyOwner] = useState(false);
  const [newOwnerInfo, setNewOwnerInfo] = useState([]);
  const [currentOwnerArray, setCurrentOwnerArray] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const query = useQuery();
  const navigate = useNavigate();
  const classes = useStyles();

  useEffect(() => {
    if (birthDateOwner > 0) {
      setBirthDate(jMoment(parseInt(birthDateOwner)));
      methods.setValue(
        'owner.birthDate',
        persianJs(jMoment(parseInt(birthDateOwner)).format('jYYYY/jMM/jDD'))
          .toEnglishNumber()
          .toString(),
      );
    } else {
      setBirthDate(birthDateOwner);
    }
    return () => { };
  }, [birthDateOwner]);

  useEffect(() => {
    if (currentOwner) {
      setCurrentOwnerArray([
        ['نام کاربری', currentOwner?.username ?? null],
        [
          'نام',
          !currentOwner?.firstName && !currentOwner?.lastName
            ? null
            : `${currentOwner?.firstName} ${currentOwner?.lastName}`,
        ],
        [
          'کد ملی',
          currentOwner?.nationalCode_verified ?? currentOwner?.nationalCode ?? null,
          !!currentOwner?.nationalCode_verified,
        ],
        [
          'شماره موبایل',
          currentOwner?.cellphoneNumber ?? currentOwner?.cellphoneNumber_unverified ?? null,
          !!currentOwner?.cellphoneNumber,
        ],
        [
          'تاریخ تولد',
          currentOwner?.legalInfoBirthDate ?? currentOwner?.birthDate
            ? jMoment(new Date(parseInt(currentOwner.birthDate))).format('jYYYY/jMM/jDD')
            : null,
          !!currentOwner?.legalInfoBirthDate,
        ],
      ]);
    }
  }, [currentOwner]);

  useEffect(() => {
    if (query) {
      if (query.get('tab') === 'owner') {
        verifyOwner(query.get('code'), query.get('state'));
      }
    }
  }, [query]);

  useEffect(() => {
    methods.register('owner.firstName');
    methods.register('owner.lastName');
    return () => {
      methods.unregister('owner.firstName');
      methods.unregister('owner.lastName');
    };
  }, [methods.register, methods.unregister]);

  // -- API REQUESTS --//
  const verifyOwner = async (ssoCode, ssoState) => {
    if (loadingVerifyOwner || !ssoCode || !ssoState) return;
    setLoadingVerifyOwner(true);

    query.delete('code');
    query.delete('state');
    navigate({
      search: query.toString(),
    });

    try {
      const params = {
        code: ssoCode,
        state: ssoState,
        redirect_url: `${process.env.REACT_APP_REDIRECT_OWNER_URI}/BusinessInfo?tab=owner`,
      };
      const res = await OwnerServices.verifyNewOwner(params);
      // eslint-disable-next-line eqeqeq
      if (res?.result?.userId == currentOwner?.userId) {
        createToast('خطا', 'کاربر انتخاب شده مالک فعلی کسب‌وکار می‌باشد!', 'danger');
        setLoadingVerifyOwner(false);
        return;
      }
      setNewOwnerInfo([
        ['نام کاربری', res?.result?.username ?? null],
        [
          'نام',
          !res?.result?.firstName && !res?.result?.lastName
            ? null
            : `${res?.result?.firstName ?? ''} ${res?.result?.lastName ?? ''}`,
        ],
        [
          'کد ملی',
          res?.result?.nationalCode_verified ?? res?.result?.nationalCode ?? null,
          !!res?.result?.nationalCode_verified,
        ],
        [
          'شماره موبایل',
          res?.result?.cellphoneNumber ?? res?.result?.cellphoneNumber_unverified ?? null,
          !!res?.result?.cellphoneNumber,
        ],
        [
          'تاریخ تولد',
          res?.result?.birthDate
            ? jMoment(new Date(parseInt(res?.result?.birthDate)))?.format('jYYYY/jMM/jDD')
            : null,
          !!res?.result?.legalInfoBirthDate,
        ],
      ]);

      setNewOwnerId(res?.result?.userId);
      onShowChangeOwner();
      setLoadingVerifyOwner(false);
    } catch (error) {
      setLoadingVerifyOwner(false);
    }
  };

  const handleSignIn = async () => {
    setLoginLoading(true);
    try {
      const param = {
        prompt: false,
        redirectUri: `${process.env.REACT_APP_REDIRECT_OWNER_URI}/BusinessInfo?tab=owner`,
      };
      const response = await AuthService.getLoginBusinessUrl(param);
      setLoginLoading(false);
      if (response?.data?.result) {
        window.location.href = response.data.result;
      } else {
        window.location.href = '/Home';
      }
    } catch (error) {
      setLoginLoading(false);
    }
  };

  // -- OTHER FUNCTIONS --//
  const handleChangeExpire = (e) => {
    setBirthDate(e);
    methods.setValue(
      'owner.birthDate',
      persianJs(jMoment(e).format('jYYYY/jMM/jDD')).toEnglishNumber().toString(),
    );
  };

  const ownerNationalCodeIsVerified = currentOwner?.nationalCode_verified;

  const ownerCellPhoneIsVerified = currentOwner?.cellphoneNumber;

  const ownerBirthDateIsVerified = currentOwner?.legalInfoBirthDate;

  return (
    <>
    
      {loadingVerifyOwner || loading ? (
        <CircularProgress size={50} />
      ) : (
        <>
          {/* Change Owner CTA */}

          {!showChangingOwner ? (
            <>
              {currentOwnerArray ? (
                <InfoBox
                  type="disable"
                  content="مالک کسب‌وکار در کسب‌وکارهای خانگی، شخصی و اصناف شخص حقیقی است که صاحب کسب‌وکار است. هر شخص حقیقی می‌تواند چند کسب‌وکار داشته باشد اما تنها یکی از آنها می‌تواند بدون کد اقتصادی باشد. با تکمیل اطلاعات هویتی مالک، می‌توانید سطوح مالی کسب‌وکار خود را ارتقا دهید."
                />
              ) : null}
              {/* TODO: Reform It */}
              {!currentOwnerArray ? (
                <b style={{ marginInlineStart: 10 }}>
                  کسب‌وکار شما فاقد مالک می‌باشد!
                  <br />
                  <br />
                </b>
              ) : null}
              <ButtonLoading
                color="primary"
                variant="outlined"
                startIcon={currentOwnerArray ? <Icons icon="Compair" color="#2b3990" size="22" /> : <Icons icon="userAdd" color="#2b3990" size="22" />}
                onClick={() => setIsConfirmModalOpen(true)}
                name={currentOwnerArray ? 'اعطای مالکیت به کاربر دیگر' : 'انتخاب کاربر مالک'}
                loading={loginLoading}
                style={{ marginInlineEnd: 10 }}
              />
            </>
          ) : (
            <>
              {/* Old Owner Info (incomming) */}
              <Typography className={classes.confirmChangeOwnerText}>{`عملیات ${currentOwnerArray ? 'انتقال' : 'اعطای'
                } مالکیت کسب‌وکار با مشخصات زیر را تایید می‌کنید:`}</Typography>
              {currentOwnerArray ? (
                <Grid
                  container
                  item
                  xs={12}
                  md={9}
                  lg={6}
                  spacing={3}
                  className={classes.ownerCard}
                >
                  <Grid item xs={12} className={classes.ownerTitleBar}>
                    <Typography className={classes.newOwnerTitle}>اطلاعات مالک فعلی</Typography>
                  </Grid>

                  {currentOwnerArray.map((v, k) => (
                    <Grid item xs={4} key={k}>
                      <Typography className={classes.newOwnerItemTitle}>{v[0] ?? ''}</Typography>
                      <Typography className={classes.newOwnerItemValue}>
                        {v[1] ? (
                          <LabelLine
                            label={v[1]}
                            justify="flex-start"
                            infoText={
                              v[2] === true ? 'تایید شده' : v[2] === false ? 'تایید نشده' : null
                            }
                            infoType={v[2] === true ? 'success' : v[2] === false ? 'warning' : null}
                          />
                        ) : (
                          '-'
                        )}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              ) : null}

              {/* New Owner Info (incomming) */}
              {newOwnerInfo ? (
                <Grid
                  container
                  item
                  xs={12}
                  md={9}
                  lg={6}
                  spacing={3}
                  className={clsx(classes.ownerCard, classes.newOwnerCard)}
                >
                  <Grid
                    item
                    xs={12}
                    className={clsx(classes.ownerTitleBar, classes.newOwnerTitleBar)}
                  >
                    <Typography className={classes.newOwnerTitle}>اطلاعات مالک جدید</Typography>
                  </Grid>
                  {newOwnerInfo.map((v, k) => (
                    <Grid item xs={4} key={k}>
                      <Typography className={classes.newOwnerItemTitle}>{v[0] ?? ''}</Typography>
                      <Typography className={classes.newOwnerItemValue}>
                        {v[1] ? (
                          <LabelLine
                            label={v[1]}
                            justify="flex-start"
                            infoText={
                              v[2] === true ? 'تایید شده' : v[2] === false ? 'تایید نشده' : null
                            }
                            infoType={v[2] === true ? 'success' : v[2] === false ? 'warning' : null}
                          />
                        ) : (
                          '-'
                        )}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              ) : null}

              <Grid style={{ marginTop: 16 }}>
                <ButtonLoading
                  variant="contained"
                  color="primary"
                  onClick={onSubmit}
                  loading={submittingBusiness}
                  name={currentOwnerArray ? 'انتقال مالکیت' : 'اعطای مالکیت'}
                />
                <Button
                  variant="contained"
                  onClick={onCancelOwnerChange}
                  style={{ marginInlineStart: 8 }}
                >
                  انصراف
                </Button>
              </Grid>
            </>
          )}

          {currentOwnerArray ? (
            <>
              <span style={{ marginLeft: 15, fontSize: 12 }}>
                سطح فعلی مالک: {currentOwner?.financialLevelSrv?.value ?? 'ندارد'}
              </span>
              {(currentOwner?.financialLevelSrv?.value === undefined ||
                currentOwner?.financialLevelSrv?.value < 4) && (
                  <a
                    href="https://levelup.wepod.ir/fa/profile/completion?theme=2b3990&logo=7RPAE26TIXJEH2PC&redirect_url=https%3A%2F%2Fpanel.pod.ir%2FBusinesses%2FBusinessInfo%3Ftab%3Downer"
                    style={{ marginBottom: 10, display: 'inline-block' }}
                  >
                    <Grid container className={classes.more}>
                      <Typography className={classes.moreTitle}>احراز هویت مالک کسب و کار</Typography>
                      <span className={classes.moreIcon} >
                        <Icons icon="ArrowLeft" color="#343434" size="22" />
                      </span>
                    </Grid>
                  </a>
                )}
            </>
          ) : null}

          {!showChangingOwner && currentOwner ? (
            <Grid container spacing={3} xs={12}>
              <Grid item xs={12} lg={6} md={8}>
                {methods.watch('owner.firstName') && methods.watch('owner.lastName') ? (
                  <Grid container item xs={12} spacing={3}>
                    <Grid item xs={12} md={6} lg={6}>
                      <InputV2
                        errors={methods.formState.errors}
                        name="owner.firstName"
                        label="نام"
                        register={methods.register}
                        disabled
                        fullWidth
                        englishNumber
                        control={methods.control}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <InputV2
                        errors={methods.formState.errors}
                        name="owner.lastName"
                        label="نام خانوادگی"
                        register={methods.register}
                        disabled
                        fullWidth
                        control={methods.control}
                      />
                    </Grid>
                  </Grid>
                ) : null}
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={12} md={6} lg={6}>
                    <InputV2
                      errors={methods.formState.errors}
                      name="owner.cellphoneNumber"
                      label="شماره موبایل"
                      infoText={ownerCellPhoneIsVerified ? 'تایید شده' : 'تایید نشده'}
                      infoType={ownerCellPhoneIsVerified ? 'success' : 'warning'}
                      register={methods.register}
                      rules={isLegal ? ['isNumeric'] : ['required', 'isNumeric']}
                      disabled={disableCellPhone || ownerCellPhoneIsVerified}
                      fullWidth
                      dir="ltr"
                      englishNumber
                      control={methods.control}
                    // farsiDigitFont={true}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <InputV2
                      errors={methods.formState.errors}
                      name="owner.username"
                      label="نام کاربری"
                      register={methods.register}
                      disabled
                      fullWidth
                      dir="ltr"
                      control={methods.control}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}>
                  <Grid item xs={12} md={6} lg={6}>
                    <InputV2
                      errors={methods.formState.errors}
                      name="owner.nationalCode"
                      label="کد ملی"
                      infoText={ownerNationalCodeIsVerified ? 'تایید شده' : 'تایید نشده'}
                      infoType={ownerNationalCodeIsVerified ? 'success' : 'warning'}
                      disabled={
                        ownerNationalCodeIsVerified
                        //  financialLevel >= 2
                      }
                      register={methods.register}
                      rules={isLegal ? ['isNationalCode'] : ['required', 'isNationalCode']}
                      fullWidth
                      dir="ltr"
                      control={methods.control}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <span style={{ display: 'none' }}>
                      <InputV2
                        errors={methods.formState.errors}
                        name="owner.birthDate"
                        label="تاریخ تولد"
                        register={methods.register}
                        disabled={
                          ownerBirthDateIsVerified
                          // financialLevel >= 3
                        }
                        fullWidth
                        control={methods.control}
                        rules={isLegal ? [] : ['required']}
                      />
                    </span>
                    <DatePickerPod
                      label="تاریخ تولد"
                      infoText={ownerBirthDateIsVerified ? 'تایید شده' : 'تایید نشده'}
                      infoType={ownerBirthDateIsVerified ? 'success' : 'warning'}
                      showAsterisk={!isLegal}
                    >
                      <DatePicker
                        variant="inline"
                        autoOk
                        inputVariant="outlined"
                        labelFunc={(date) => (date ? date.format('jYYYY/jMM/jDD') : '')}
                        disabled={
                          ownerBirthDateIsVerified
                          // financialLevel >= 3
                        }
                        value={birthDate}
                        onChange={handleChangeExpire}
                        helperText={methods?.errors?.owner?.birthDate?.message}
                      />
                    </DatePickerPod>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={6} md={4}>
                {Object.keys(unverifiedOwner).length === 0 ? (
                  ''
                ) : (
                  <Alert severity="warning" className={classes.alert}>
                    <Icons icon="WarningTriangle" color="#E03E1A" size="28" />
                    <div className={classes.content}>
                      <h3 className={classes.title}>توجه</h3>
                      <p className={classes.textWrapper}>
                        «{unverifiedOwner?.name}» به عنوان مالک جدید ثبت شده است و در حال بررسی و
                        تایید توسط تیم پشتیبانی است.
                      </p>
                    </div>
                  </Alert>
                )}
              </Grid>
            </Grid>
          ) : (
            unverifiedOwner && (
              <Grid
                container
                spacing={3}
                xs={12}
                style={{ display: 'flex', justifyContent: 'end' }}
              >
                <Grid item xs={12} lg={6} md={4} className={classes.alertContainer}>
                  {Object.keys(unverifiedOwner).length === 0 ? (
                    ''
                  ) : (
                    <Alert severity="warning" className={classes.customAlert}>
                      <h3 className={classes.title}>توجه</h3>
                      <div className={classes.textWrapper}>
                        «{unverifiedOwner?.name}» به عنوان مالک جدید ثبت شده است و در حال بررسی و
                        تایید توسط تیم پشتیبانی است.
                      </div>
                    </Alert>
                  )}
                </Grid>
              </Grid>
            )
          )}
        </>
      )}

      <ConfirmDialog
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleSignIn}
        title="توجه"
        loading={loginLoading}
        confirmButtonText="بله، انتخاب کاربر مالک"
        cancelButtonProps="انصراف"
      >
        <b>
          در صورتی که هر یک از حساب‌های این کسب‌وکار دارای شماره شبا باشند، با تغییر مالک، حذف
          خواهند شد. آیا از تغییر مالک کسب‌وکار اطمینان دارید؟
        </b>
        <br />
        <br />
        پس از انتقال به صفحه ورود، با حساب کاربری مالک جدید وارد شوید.
      </ConfirmDialog>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  alert: {
    '&.MuiAlert-standardWarning': {
      backgroundColor: '#fad9d161',
      borderRadius: '8px',
      border: 'solid 1px #FAD9D1',
      display: 'flex',
      alignItems: 'center',
      '& .MuiAlert-icon': {
        fontSize: '30px',
        color: '#E03E1A',
        display: 'none',
      },
      '& .MuiAlert-message': {
        display: 'flex',
        gap: '12px',
      },
    },
  },
  content: {
    '& h3': {},
    '& p': {
      fontSize: '13px',
    },
  },
  ownerTitleBar: {
    borderBottom: `1px solid rgba(109, 114, 120, 0.18)`,
  },
  newOwnerTitleBar: {
    borderBottom: `1px solid ${theme.palette.primary.main}`,
  },
  newOwnerTitle: {
    color: '#1a334d',
    fontSize: 14,
    fontWeight: 'bold',
  },
  newOwnerItemTitle: {
    color: '#546e7a',
    fontSize: 12,
  },
  newOwnerItemValue: {
    color: '#003a52',
    fontSize: 14,
    fontWeight: 'bold',
  },
  ownerCard: {
    background: '#eef1f5',
    borderRadius: 4,
    border: '1px solid rgba(109, 114, 120, 0.18)',
    margin: theme.spacing(1, 0),
    boxSizing: 'border-box',
  },
  newOwnerCard: {
    background: theme.palette.primary.light,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  changeOwnerButton: {
    padding: theme.spacing(0, 2),
    marginInlineEnd: theme.spacing(1),
    border: 'none',
    background: theme.palette.primary.light,
    '&:hover': {
      background: theme.palette.primary.light,
      border: 'none',
    },
  },
  confirmChangeOwnerText: {
    fontSize: 14,
    fontWeight: 'bold',
    marginInlineStart: theme.spacing(2),
  },
  moreTitle: {
    fontSize: 12,
    fontWeight: 'normal',
    color: theme.palette.primary.main,
    marginInlineEnd: 5,
  },
  moreIcon: {
    fontSize: 20,
    color: theme.palette.primary.main,
    transform: 'translateX(0px)',
    transition: 'ease transform .2s',
  },
  more: {
    '&:hover svg': {
      transform: 'translateX(3px)',
    },
  },
  alertContainer: {
    display: 'flex',
    flexDirection: 'row',
    // alignItems: '',
    justifyContent: 'center',
  },
  customAlert: {
    [theme.breakpoints.up('xs')]: {
      width: '100%',
      height: 'fit-content',
    },
    [theme.breakpoints.up('md')]: {
      width: '100%',
      height: 'fit-content',
      paddingBottom: '5%',
      marginTop: '5%',
    },
  },
}));

export default OwnerInfo;
